<template>
  <CustomRow>
    <v-card class="mt-5 text-center cusPadding">
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="10" sm="8" md="4">
          <div class="text-page-title font-weight-bold">
            {{ $t("forgot_pwd_title") }}
          </div>
          <v-form v-model="isFormValid">
            <VuePhoneNumberInput
              class="mb-4"
              required
              size="lg"
              :no-flags="true"
              :error-color="'#ff0032'"
              :error="invalidMember || isPhoneNumberError"
              :translations="{
                countrySelectorLabel: $t('country_code'),
                phoneNumberLabel: $t('login_phone_field')
              }"
              :no-example="true"
              v-model="phoneNumberWithCode"
              @update="phoneNuberUpdate"
              @phone-number-blur="phoneNuberBlur"
            />
            <div class="v-text-field__details pl-3">
              <div class="v-messages theme--light error--text" role="alert">
                <div class="v-messages__wrapper">
                  <div
                    v-if="isShowPhoneNumberWarning"
                    class="v-messages__message"
                  >
                    {{ $t("input_required") }}
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="country !== 'TW'"
              class="v-alert p-4 text-left red--text pink-1 body-2 font-weight-regular rounded-lg"
              v-html="$t('mobile_hint')"
            ></div>
            <v-text-field
              v-model="email"
              class="mt-4"
              label="E-mail"
              outlined
              :hint="$t('forgot_pwd_email_required')"
              color="primary-1"
              :rules="[inputRules.required, inputRules.email]"
              :error-messages="getErrorMessage()"
              @input="inputEmailField"
            >
              <template #message="{ message }">
                {{ $t(message) }}
              </template>
            </v-text-field>
            <v-btn
              class="mt-8 font-weight-bold nextBtn"
              :class="{
                disabledBtn:
                  !isFormValid || !loadingFinished || isPhoneNumberError
              }"
              :disabled="!isFormValid || !loadingFinished || isPhoneNumberError"
              block
              depressed
              x-large
              @click="sendEmailVerify"
            >
              <template v-if="!loadingFinished">
                <v-progress-circular
                  indeterminate
                  color="white"
                  size="25"
                  width="2"
                ></v-progress-circular>
              </template>
              <template v-else> {{ $t("forgot_pwd_get_otp") }} </template>
            </v-btn>
          </v-form>
        </v-col>
        <v-spacer></v-spacer>
      </v-row>
    </v-card>
  </CustomRow>
</template>

<script>
import UM from "@/api/user-management";
import inputRulesJS from "@/shared/inputRules.js";
import { mapActions } from "vuex";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";

export default {
  data() {
    return {
      isFormValid: false,
      phoneNumberWithCode: "",
      country: "",
      countryCode: "",
      mobile: "",
      email: "",
      inputRules: {
        required: inputRulesJS.required,
        email: inputRulesJS.email
      },
      invalidMember: false,
      invalidEmail: false,
      loadingFinished: true,
      isPhoneNumberError: false,
      isShowPhoneNumberWarning: false
    };
  },
  components: {
    VuePhoneNumberInput
  },
  metaInfo() {
    return {
      title: `${this.$t("forgot_pwd_title")} - ${this.$t("app_name")}`,
      script: [
        {
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: `${this.$t("forgot_pwd_title")} - ${this.$t("app_name")}`,
            url: window.location.href
          }
        }
      ]
    };
  },
  methods: {
    ...mapActions({
      showDialog: "dialog/show"
    }),
    async sendEmailVerify() {
      this.loadingFinished = false;
      const { data } = await UM.sendForgetPassword(
        this.countryCode,
        this.mobile.replace(/\D/g, ""),
        this.email
      );

      switch (data.errCode) {
        case "00":
          this.$router
            .push({
              name: "HasSendPassword",
              params: {
                countryCode: this.countryCode,
                mobile: this.mobile.replaceAll(/\D/g, ""),
                email: this.email
              }
            })
            .catch(() => {});
          break;
        case "101":
          this.invalidEmail = true;
          break;
        case "102":
          this.showDialog({
            alert: true,
            content: `<div class="pa-6 text-center font-weight-bold">
            <img
              src="/image/ic-warning@3x.png"
              alt=""
              style="width:80px"
            />
            <div class="mt-5">${this.$t("forgot_pwd_error")}<br>${
              data.errDetail
            }</div>
          </div>`
          });
          break;
        case "104":
          this.invalidMember = true;
          break;
        default:
          break;
      }
      this.loadingFinished = true;
    },
    inputEmailField() {
      this.invalidEmail = false;
    },
    getErrorMessage() {
      if (this.invalidMember === true) {
        return this.$t("forgot_pwd_not_signup");
      } else if (this.invalidEmail === true) {
        return this.$t("forgot_pwd_email_invalid");
      } else {
        return "";
      }
    },
    phoneNuberBlur() {
      if (!this.phoneNumberWithCode) {
        this.isPhoneNumberError = true;
        this.isShowPhoneNumberWarning = true;
      }
    },
    phoneNuberUpdate(phoneNumberObject) {
      this.invalidMember = false;
      this.mobile = phoneNumberObject?.phoneNumber || "";
      this.countryCode = phoneNumberObject?.countryCallingCode || "";
      this.country = phoneNumberObject?.countryCode || "";

      // UI 定義的特殊行為，當初始顯示時不要檢查是否錯誤，維持元件的框為黑色，
      // 但是元件 init 後顯示時會自動呼叫 updatd event，導致初始顯示時就會當成驗證失敗的狀態，
      // bypass 沒輸入電話時的檢查行為
      if (
        this.phoneNumberWithCode === "" &&
        phoneNumberObject?.phoneNumber === undefined
      ) {
        return;
      }

      if (!this.mobile || phoneNumberObject?.isValid === false) {
        this.isPhoneNumberError = true;
      } else {
        this.isPhoneNumberError = false;
      }
      if (phoneNumberObject?.phoneNumber === undefined) {
        this.isShowPhoneNumberWarning = true;
      } else {
        this.isShowPhoneNumberWarning = false;
      }
    },
    getCountryCode() {
      const countrySelect = document.querySelector(".country-selector__input");

      if (!countrySelect) {
        setTimeout(() => {
          this.getCountryCode();
        }, 300);

        return;
      }
      this.countryCode = countrySelect.value;
      countrySelect.addEventListener("change", e => {});
    }
  },
  mounted() {
    this.getCountryCode();
    this.$gtm.trackView(`ForgotPassword`, this.$route.path);
  }
};
</script>

<style lang="scss" scoped>
.cusPadding {
  padding: 80px 20px;
}
</style>
